import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Select, ButtonGroup, Flex, OptionDropdown } from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import { simulationChartType, timeCostView } from 'src/enums/simulations';
import { getOperations, getSections } from 'store/entities/activity-model/activity-model';
import { useParams } from 'react-router';
import {
  dataTypeUpdated,
  viewTypeUpdated,
  timeCostTypeUpdated,
} from 'src/store/entities/ui/simulations/chart-controls';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { getCostCodesItems } from 'src/store/entities/cost-codes/cost-codes';
import ResultChart from './result-chart';
import ResultTable from './result-table';
import { selectOptions } from './result-utils';
import './style.less';

const ResultContent = ({
  simulationsResult,
  getOperations,
  getSections,
  chartKey,
  dataTypeUpdated,
  viewTypeUpdated,
  timeCostTypeUpdated,
  charts,
  operations,
  sectionItems,
  getCostCodesItems,
}) => {
  const { t } = useTranslation();
  const { project } = useParams();

  useEffect(() => {
    if (project) {
      if (operations.length === 0) {
        getOperations(project);
        getCostCodesItems();
      }
      if (sectionItems.length === 0) {
        getSections(project);
      }
    }
  }, [project]);

  const riskOperationOptions = operations.map((operation) => ({
    value: operation.operationId,
    label: operation.name,
    selected: true,
  }));
  const riskSectionOptions = sectionItems.map((section) => ({
    value: section.projectSectionId,
    label: section.name,
    selected: true,
  }));
  const riskFilterOptions = [
    {
      type: 'Heading',
      label: t(translations.operations),
    },
    ...riskOperationOptions,
    {
      type: 'Divider',
    },
    {
      type: 'Heading',
      label: t(translations.sections),
    },
    ...riskSectionOptions,
  ];

  const [riskFilter, setRiskFilter] = useState(riskFilterOptions);

  useEffect(() => {
    setRiskFilter(riskFilterOptions);
  }, [operations, sectionItems]);

  const controlsChartPortalId = `result-chart-controls-${chartKey}`;
  const controlsTablePortalId = `result-table-controls-${chartKey}`;

  const chartTypeIndices = {
    [simulationChartType.ACTIVITY_DURATIONS]: 0,
    [simulationChartType.SECTION_DURATIONS]: 0,
    [simulationChartType.COST_ITEM_OVERVIEW]: 1,
    [simulationChartType.AFE_REPORT]: 1,
    [simulationChartType.MODIFIERS]: 0,
    [simulationChartType.SECTION_WATERFALL]: 0,
  };

  const viewTypeIndices = {
    [simulationChartType.ACTIVITY_DURATIONS]: 1,
    [simulationChartType.MODIFIERS]: 0,
    [simulationChartType.SECTION_WATERFALL]: 0,
    [simulationChartType.PROBABILITY_DENSITY]: 0,
    [simulationChartType.AFE_REPORT]: 1,
  };

  const showTime =
    charts[chartKey]?.dataType?.value !== simulationChartType.COST_ITEM_OVERVIEW &&
    charts[chartKey]?.dataType?.value !== simulationChartType.RISKS &&
    charts[chartKey]?.dataType?.value !== simulationChartType.AFE_REPORT;

  const showCost =
    charts[chartKey]?.dataType?.value !== simulationChartType.ACTIVITY_DURATIONS &&
    charts[chartKey]?.dataType?.value !== simulationChartType.SECTION_DURATIONS &&
    charts[chartKey]?.dataType?.value !== simulationChartType.RISKS &&
    charts[chartKey]?.dataType?.value !== simulationChartType.MODIFIERS &&
    charts[chartKey]?.dataType?.value !== simulationChartType.AFE_REPORT;

  const showChart =
    charts[chartKey]?.dataType?.value !== simulationChartType.ACTIVITY_DURATIONS &&
    charts[chartKey]?.dataType?.value !== simulationChartType.AFE_REPORT;

  const showTable = charts[chartKey]?.dataType?.value !== simulationChartType.MODIFIERS;

  const showFilter = charts[chartKey]?.dataType?.value === simulationChartType.RISKS;

  const updateDataType = (value) => {
    const index = selectOptions.findIndex((el) => el.value === value);
    const viewTypeValue = viewTypeIndices[value] !== undefined ? viewTypeIndices[value] : charts[chartKey].viewType;
    const chartTypeValue =
      chartTypeIndices[value] !== undefined ? chartTypeIndices[value] : charts[chartKey].timeCostType;
    viewTypeUpdated({
      key: chartKey,
      values: viewTypeValue,
    });
    timeCostTypeUpdated({
      key: chartKey,
      values: chartTypeValue,
    });
    dataTypeUpdated({ key: chartKey, values: selectOptions[index] });
  };
  return (
    <div className="content-container">
      <Flex gap justifyContent="space-between">
        <Flex gap="12px" alignItems="flex-start">
          <Select
            testId="result-overview-select-chart-type"
            name="result-overview-select"
            width="auto"
            options={selectOptions}
            small
            onChange={(ev) => updateDataType(ev.target.value)}
            value={selectOptions[selectOptions.findIndex((item) => item.value === charts[chartKey].dataType.value)]}
          />
          <ButtonGroup
            testId="result-overview-button-group-view-mode"
            items={[
              ...(showChart
                ? [
                    {
                      testId: 'chart',
                      key: 0,
                      icon: 'chart',
                      disabled: charts[chartKey].dataType.value === simulationChartType.ACTIVITY_DURATIONS,
                    },
                  ]
                : []),
              ...(showTable
                ? [
                    {
                      testId: 'table',
                      key: 1,
                      icon: 'table',
                      disabled: charts[chartKey].dataType.value === simulationChartType.PROBABILITY_DENSITY,
                    },
                  ]
                : []),
            ]}
            small
            onSelected={(value) => viewTypeUpdated({ key: chartKey, values: value })}
            value={charts[chartKey].viewType ? 1 : 0}
          />
          <ButtonGroup
            testId="result-overview-button-group-time-cost-type"
            items={[
              ...(showTime
                ? [
                    {
                      testId: 'time',
                      key: 0,
                      label: t(translations.time),
                      value: timeCostView.TIME,
                    },
                  ]
                : []),
              ...(showCost
                ? [
                    {
                      testId: 'cost',
                      key: 1,
                      label: t(translations.cost),
                      value: timeCostView.COST,
                    },
                  ]
                : []),
            ]}
            small
            onSelected={(value) => timeCostTypeUpdated({ key: chartKey, values: value })}
            value={charts[chartKey].timeCostType}
          />
          {showFilter && (
            <OptionDropdown
              small
              label="Filter"
              options={riskFilter}
              onChange={(event) => setRiskFilter(event.target.value)}
              showHeader={false}
            />
          )}
        </Flex>
        {charts[chartKey].viewType ? <div id={controlsTablePortalId} /> : <div id={controlsChartPortalId} />}
      </Flex>
      <div className="content-inner">
        {charts[chartKey].viewType ? (
          <div className="content-inner-table">
            <ResultTable
              simulationsResult={simulationsResult}
              selectedItem={charts[chartKey].dataType}
              selectedDataEntity={charts[chartKey].timeCostType}
              controlsPortalId={controlsTablePortalId}
              riskFilter={riskFilter}
            />
          </div>
        ) : (
          <div className="content-inner-chart">
            <ResultChart
              key={`${charts[chartKey].dataType.value}${charts[chartKey].timeCostType}`}
              selectedItem={charts[chartKey].dataType}
              selectedDataEntity={charts[chartKey].timeCostType}
              simulationsResult={simulationsResult}
              controlsPortalId={controlsChartPortalId}
              riskFilter={riskFilter}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = ({ entities }) => {
  const {
    activityModel: { operations, sectionItems },
  } = entities;

  return {
    operations,
    sectionItems,
  };
};

const mapDispatchToProps = {
  getOperations,
  getSections,
  dataTypeUpdated,
  viewTypeUpdated,
  timeCostTypeUpdated,
  getCostCodesItems,
};

export default withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(ResultContent));
